import talk2Api from 'src/plugins/api/api'
import { CALENDAR_OAUTH_MESSAGE_SOURCE, CalendarOAuthPopupStatus } from 'src/constants/calendar-connections'

const POPUP_CLOSED_POLL_INTERVAL_MILLISECONDS = 500
const POPUP_WIDTH = 600
const POPUP_HEIGHT = 700
const POPUP_LEFT = 200
const POPUP_TOP = 100
const POPUP_WINDOW_FEATURES = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${POPUP_LEFT},top=${POPUP_TOP}`
const OAUTH_POPUP_TIMEOUT_MILLISECONDS = 5 * 60 * 1000

export default {
  methods: {
    /**
     * Opens the driver OAuth authorization URL in a popup and resolves once the connection
     * is created.
     *
     * ponytail: api-core's OAuth callback view posts the result back to `window.opener` with
     * a `targetOrigin` pinned to api-core's own APP_URL (see config/calendarv2.php
     * `oauth_base_url`), not to the caller's actual origin. Talk runs on a different origin
     * than api-core, so the browser silently drops that postMessage and it never reaches this
     * listener in practice. Resolution here relies on the popup-closed poll below, which diffs
     * the connections list instead. Upgrade path: api-core's oauth-popup Blade view needs to
     * target the caller's real origin (e.g. echoed back through the signed OAuth state) before
     * the postMessage branch below can be trusted for cross-origin callers like Talk.
     */
    openCalendarOAuthPopup (driver, visibility, existingConnections) {
      return new Promise((resolve, reject) => {
        const popup = window.open('about:blank', 'calendar_oauth_popup', POPUP_WINDOW_FEATURES)

        if (!popup) {
          reject(new Error('We couldn\'t open the calendar connection window. Please allow pop-ups and try again.'))
          return
        }

        const existingConnectionIds = existingConnections.map(connection => connection.id)
        const allowedMessageOrigin = new URL(process.env.API_URL).origin

        let messageListener = null
        let pollIntervalId = null
        let timeoutId = null
        let settled = false

        const teardown = () => {
          if (messageListener) {
            window.removeEventListener('message', messageListener)
            messageListener = null
          }

          if (pollIntervalId) {
            clearInterval(pollIntervalId)
            pollIntervalId = null
          }

          if (timeoutId) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
        }

        const settleResolve = (connection) => {
          if (settled) {
            return
          }

          settled = true
          teardown()

          if (popup && !popup.closed) {
            popup.close()
          }

          resolve(connection)
        }

        const settleReject = (message) => {
          if (settled) {
            return
          }

          settled = true
          teardown()

          if (popup && !popup.closed) {
            popup.close()
          }

          reject(new Error(message))
        }

        timeoutId = setTimeout(() => {
          settleReject(new Error('Calendar connection timed out. Please try again.'))
        }, OAUTH_POPUP_TIMEOUT_MILLISECONDS)

        const findNewConnection = async () => {
          const response = await talk2Api.V2.calendarConnections.get()
          const connections = response.data.data

          return connections.find(connection =>
            !existingConnectionIds.includes(connection.id) &&
            connection.driver === driver &&
            connection.visibility === visibility
          )
        }

        messageListener = (event) => {
          if (event.origin !== allowedMessageOrigin) {
            return
          }

          if (!event.data || event.data.source !== CALENDAR_OAUTH_MESSAGE_SOURCE) {
            return
          }

          if (event.data.status === CalendarOAuthPopupStatus.ERROR) {
            settleReject(event.data.message || 'The calendar connection could not be completed.')
            return
          }

          if (event.data.status === CalendarOAuthPopupStatus.CONNECTED) {
            findNewConnection()
              .then(connection => settleResolve(connection || null))
              .catch(() => settleResolve(null))
          }
        }

        window.addEventListener('message', messageListener)

        pollIntervalId = setInterval(() => {
          if (!popup || !popup.closed) {
            return
          }

          clearInterval(pollIntervalId)
          pollIntervalId = null

          findNewConnection()
            .then(connection => settleResolve(connection || null))
            .catch(() => settleResolve(null))
        }, POPUP_CLOSED_POLL_INTERVAL_MILLISECONDS)

        talk2Api.V2.calendarConnections.getAuthorizationUrl(driver, visibility)
          .then(response => {
            if (popup) {
              popup.location = response.data.data.url
            }
          })
          .catch(error => {
            settleReject(error.response?.data?.error || 'Unable to start the calendar connection. Please try again.')
          })
      })
    }
  }
}
